import React from 'react';

/**
 * Phase 3.5 — broadcast-style "HEAD TO HEAD" rivalry chip.
 *
 * Phase 3.5.8: removed Draws from all player-facing UI. Kickard ties
 * are resolved by penalties so there is no final draw result in normal
 * play. Wins now include a penalty-win sub-count, displayed as
 * `4 (1P)` where "P" = penalty wins. Last-match copy says
 * "won on penalties" when applicable.
 *
 * Backend payload still includes `draws` for backwards compatibility,
 * but it is intentionally NOT rendered.
 *
 * Two visual variants via `compact` prop:
 *   • compact=true   → single-line chip (good for in-match / kickoff)
 *   • compact=false  → 2-tile broadcast card (good for waiting room +
 *                      match summary). Also shows last-match line.
 */
export default function H2HCard({
  h2h,              // { myWins, opponentWins, myPenaltyWins, opponentPenaltyWins, opponentName, lastMatch }
  myName,           // string — local guest's displayName
  opponentName,     // string fallback
  compact = false,
  showLast = true,
}) {
  if (!h2h) return null;
  const oppName = h2h.opponentName || opponentName || 'Opponent';
  const myWins = h2h.myWins || 0;
  const oppWins = h2h.opponentWins || 0;
  const myPenWins = h2h.myPenaltyWins || 0;
  const oppPenWins = h2h.opponentPenaltyWins || 0;
  // `draws` intentionally NOT read — kept on the API for back-compat but
  // never displayed.
  const totalMatches = myWins + oppWins;

  const formatWins = (w, p) => `${w} (${p}P)`;

  let summaryLine;
  if (totalMatches === 0) {
    summaryLine = 'First meeting';
  } else if (myWins > oppWins) {
    summaryLine = `${myName || 'You'} leads ${formatWins(myWins, myPenWins)} — ${formatWins(oppWins, oppPenWins)}`;
  } else if (oppWins > myWins) {
    summaryLine = `${oppName} leads ${formatWins(oppWins, oppPenWins)} — ${formatWins(myWins, myPenWins)}`;
  } else {
    summaryLine = `All square ${formatWins(myWins, myPenWins)} — ${formatWins(oppWins, oppPenWins)}`;
  }

  if (compact) {
    // Pre-match lower-third compact chip — keep it short.
    const compactLine = totalMatches === 0
      ? 'First meeting'
      : `${formatWins(myWins, myPenWins)} — ${formatWins(oppWins, oppPenWins)}`;
    return (
      <div
        data-testid="h2h-chip"
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(var(--k-gold)/0.55)] bg-black/45"
      >
        <span className="font-heading text-[9px] tracking-[0.32em] uppercase text-[hsl(var(--k-gold-bright))]">
          H2H
        </span>
        <span data-testid="h2h-summary" className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/75 truncate">
          {compactLine}
        </span>
      </div>
    );
  }

  const lastDecidedByPenalty = !!(h2h.lastMatch && (h2h.lastMatch.decidedByPenalty || h2h.lastMatch.endedBy === 'penalty'));

  return (
    <div
      data-testid="h2h-card"
      className="w-full rounded-xl border border-[hsl(var(--k-line))] bg-[hsl(var(--k-bg-elev)/0.85)] px-4 py-3"
    >
      <div className="flex items-center gap-3">
        <span className="block w-5 h-px bg-[hsl(var(--sb-rule)/0.55)]" />
        <span className="font-heading text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--k-gold-bright))]">
          Head to Head
        </span>
        <span className="block flex-1 h-px bg-[hsl(var(--sb-rule)/0.25)]" />
      </div>

      {/* Two-tile (no draws). Each tile shows total wins + (NP) sub-count. */}
      <div className="grid grid-cols-2 gap-2 mt-3 items-end">
        <div className="text-center">
          <div className="flex items-baseline justify-center gap-1">
            <span data-testid="h2h-my-wins" className="font-heading text-3xl text-[hsl(var(--k-gold-bright))] leading-none">
              {myWins}
            </span>
            <span data-testid="h2h-my-penalty-wins" className="font-mono text-[10px] text-white/55 tracking-[0.12em]">
              ({myPenWins}P)
            </span>
          </div>
          <div className="font-mono text-[9px] text-white/45 tracking-[0.22em] uppercase mt-1 truncate">
            {myName || 'YOU'}
          </div>
        </div>
        <div className="text-center">
          <div className="flex items-baseline justify-center gap-1">
            <span data-testid="h2h-opponent-wins" className="font-heading text-3xl text-[hsl(var(--k-red))] leading-none">
              {oppWins}
            </span>
            <span data-testid="h2h-opponent-penalty-wins" className="font-mono text-[10px] text-white/55 tracking-[0.12em]">
              ({oppPenWins}P)
            </span>
          </div>
          <div className="font-mono text-[9px] text-white/45 tracking-[0.22em] uppercase mt-1 truncate">
            {oppName}
          </div>
        </div>
      </div>

      <div
        data-testid="h2h-summary"
        className="mt-3 font-mono text-[10px] text-white/65 tracking-[0.22em] uppercase text-center"
      >
        {summaryLine}
      </div>

      {/* Goals line + tiny "P = penalty wins" legend. */}
      {totalMatches > 0 && (
        <div className="mt-1 flex items-center justify-between gap-2 font-mono text-[9px] text-white/40 tracking-[0.18em] uppercase">
          <span data-testid="h2h-goals">
            Goals {h2h.myGoals ?? 0}—{h2h.opponentGoals ?? 0}
          </span>
          <span data-testid="h2h-legend">P = penalty wins</span>
        </div>
      )}

      {showLast && h2h.lastMatch && (
        <div data-testid="h2h-last-match" className="mt-2 pt-2 border-t border-white/8 font-mono text-[9px] text-white/45 tracking-[0.18em] uppercase text-center truncate">
          Last:{' '}
          <span className={
            h2h.lastMatch.result === 'win' ? 'text-[hsl(var(--k-gold-bright))]'
              : h2h.lastMatch.result === 'loss' ? 'text-[hsl(var(--k-red))]'
              : 'text-white/55'
          }>
            {h2h.lastMatch.result === 'win'
              ? `${myName || 'You'} won${lastDecidedByPenalty ? ' on penalties' : ''}`
              : h2h.lastMatch.result === 'loss'
                ? `${oppName} won${lastDecidedByPenalty ? ' on penalties' : ''}`
                : 'Drew'}
          </span>
          {' '}{h2h.lastMatch.myScore}—{h2h.lastMatch.opponentScore}
          {h2h.lastMatch.penaltyScore && (
            <> · pens {h2h.lastMatch.penaltyScore.mine}—{h2h.lastMatch.penaltyScore.opponent}</>
          )}
        </div>
      )}
    </div>
  );
}
